/**
 * Store sesi level-modul untuk KamusBari.
 *
 * TUJUAN: decoupling. `App.tsx` (route guard) dan komponen mana pun bisa
 * subscribe ke sesi tanpa saling import satu sama lain, dan tanpa hook global
 * yang belum ada. Kalau nanti ada `src/lib/useAuth.ts`, hook itu boleh
 * memanggil `session.getUser()` + `session.subscribe()` sebagai sumber tunggal
 * kebenaran — file ini tetap jadi owners state-nya.
 *
 * PERILAKU:
 * - `user` adalah state yang bisa diamati (observable), di-back oleh satu
 *   variabel modul yang di-cache (`currentUser`).
 * - `subscribe(fn)` mendaftarkan listener; dipanggil dengan user saat ini
 *   LANGSUNG (rehydrasi awal), lalu lagi setiap `setUser` yang mengubah nilai.
 * - `setUser` diberi user yang secara Objects.is sama dengan nilai lama → tidak
 *   ada notifikasi (cegah render loop dari StrictMode / effect yang berulang).
 * - Tidak ada async di sini. Pemuatan user dari `/api/auth/me` dilakukan agent
 *   auth/API client, yang memanggil `setUser` setelah hasilnya diketahui.
 */

/** Pengguna yang sedang masuk. Bentuknya sengaja sama dengan `user` di API. */
export interface SessionUser {
  id: string
  email: string
  display_name: string
  /** Umumnya 'user' atau 'admin'. */
  role: string
}


/**
 * True kalau pengguna punya peran admin penuh.
 *
 * Peran 'moderator' SENGAJA tidak termasuk di sini: moderator boleh menilai
 * entri dan pengajuan bahasa, tapi tidak boleh mengubah data atau menyentuh
 * pengguna. Panel yang memanggil ini harus disembunyikan untuk moderator, bukan
 * sekadar dimatikan setelah tampil - kalau terlihat tapi tidak bisa dipakai,
 * itu terlihat seperti galat.
 */
export function getSessionUser(): SessionUser | null {
  return currentUser
}

export function isAdminRole(user?: { role?: string } | null): boolean {
  return user?.role === 'admin'
}

/** True kalau pengguna boleh masuk ke panel admin sama sekali. */
export function isStaffRole(user?: { role?: string } | null): boolean {
  return user?.role === 'admin' || user?.role === 'moderator'
}
/**
 * Listener sesi: dipanggil dengan user saat ini setiap kali berubah.
 *
 * Listener menerima DUA nilai: user dan apakah pemeriksaan sudah selesai.
 *Parameter `checked` penting: selama `false`, `null` belum berarti
 * "tidak login" - dia hanya berarti "belum tahu". Guard route memakai ini,
 * kalau tidak user yang punya sesi sah akan dikick ke halaman login hanya
 * karena halaman kebetulan ter-render sebelum `/auth/me` selesai dijawab.
 */
export type SessionListener = (user: SessionUser | null, checked: boolean) => void

// Cache nilai saat ini. Di luar `session` supaya tidak bisa diubah langsung.
let currentUser: SessionUser | null = null

/**
 * Sudah pernah ditanya `/auth/me` pada sesi halaman ini? Cache modul, bukan
 * per-komponen: dua pemanggil harus dapat jawaban yang sama, dan pemeriksaan
 * kedua hanya menambah request tanpa menambah pengetahuan.
 */
let checked = false

const listeners = new Set<SessionListener>()

/** Ambil user saat ini, atau `null` kalau belum login / belum dimuat. */
export function getUser(): SessionUser | null {
  return currentUser
}

/**
 * Set user saat ini dan beri tahu semua listener kalau nilainya berubah.
 * Pakai `setUser(null)` saat logout atau setelah session cookie kedaluwarsa.
 */
export function setUser(user: SessionUser | null): void {
  // Setiap setUser juga berarti "sudah diketahui" - pemanggilnya sudah punya
  // jawaban. Ini satu-satunya cara store ini bisa jadi sumber kebenaran
  // tunggal tanpa flag kedua yang bisa lupa diset.
  const changed = !Object.is(currentUser, user)
  const checkChanged = !checked
  if (!changed && !checkChanged) return

  currentUser = user
  checked = true

  // Salin dulu: listener boleh unsubscribe saat dipanggil.
  for (const listener of [...listeners]) listener(currentUser, checked)
}

/**
 * Tandai pemeriksaan selesai tanpa mengubah user. Dipakai setelah `/auth/me`
 * menjawab, baik isinya user maupun null (401 = memang tidak login, itu
 * jawaban yang sah).
 */
export function markChecked(): void {
  if (checked) return
  checked = true
  for (const listener of [...listeners]) listener(currentUser, checked)
}

/** Sudah selesai ditanya server? */
export function isChecked(): boolean {
  return checked
}

/**
 * Kosongkan store. Hanya untuk test: produksi memakai `logout()` yang
 * memanggil `setUser(null)`.
 */
export function reset(): void {
  currentUser = null
  checked = false
  for (const listener of [...listeners]) listener(currentUser, checked)
}

/**
 * Dengarkan perubahan sesi. Listener langsung dipanggil satu kali dengan nilai
 * saat ini supaya komponen bisa render state yang benar sejak frame pertama
 * tanpa perlu efek tambahan. Balikin fungsi unsubscribe.
 */
export function subscribe(listener: SessionListener): () => void {
  listeners.add(listener)
  listener(currentUser, checked)
  return () => {
    listeners.delete(listener)
  }
}

/** Convenience: objek yang mengekspos API store di atas. */
export const session = {
  getUser,
  setUser,
  markChecked,
  isChecked,
  subscribe,
}

/** SSR/test helper: kosongkan store dan semua listener (Testing Library). */
export function resetSession(): void {
  reset()
  listeners.clear()
}

/**
 * Tanya server apakah ada sesi aktif, lalu perbarui store.
 *
 * Fungsi ini dulu terduplikasi di `components/Layout.tsx` dan
 * `admin/AdminShell.tsx`. Duplikasi itu rapuh: saat panel admin dipindahkan
 * keluar dari Layout, salinannya di AdminShell tidak ikut - hasilnya
 * "Memeriksa sesi admin..." menggantung selamanya karena `checked` tidak pernah
 * naik dan tidak ada satu pun yang memanggil `/api/auth/me`.
 *
 * Satu fungsi = satu tempat yang wajib benar.
 *
 * Mengembalikan true kalau sesi sudah diketahui (sukses maupun gagal), supaya
 * pemanggil tahu tidak perlu mencoba lagi.
 */
export async function rehydrate(): Promise<boolean> {
  // Sudah diketahui: jangan query ulang. `isChecked()` (bukan `getUser()`)
  // adalah syaratnya - `getUser()` bisa non-null setelah halaman login mengisi
  // store, sementara `checked` sudah true juga di sana, tapi hanya `checked`
  // yang berarti "server sudah menjawab".
  if (checked) return true

  try {
    const res = await fetch('/api/auth/me', { credentials: 'include' })
    const body = (await res.json()) as { user?: unknown }
    // Jawaban "tidak login" juga harus menandai checked = true; kalau tidak,
    // guard route menggantung di "memuat" selamanya.
    setUser((body?.user as SessionUser | undefined) ?? null)
    return true
  } catch {
    // Offline / server tak terjangkau: TIDAK boleh dianggap "tidak login".
    // Cookie masih ada, jadi tandai sudah dicek tapi biarkan user apa adanya
    // supaya gangguan jaringan sesaat tidak memaksa login ulang.
    markChecked()
    return true
  }
}
